import { authFetch } from '../utils/auth';
import API_CONFIG from '../config/api';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import useNetworkHierarchy from '../hooks/useNetworkHierarchy';
import { canUserViewPratica } from '../utils/praticaAuth';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  EyeIcon,
  ChevronRightIcon,
  CalendarIcon,
  UserIcon,
  TruckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const PratichePage = () => {
  const [pratiche, setPratiche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filtroStato, setFiltroStato] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPratiche, setTotalPratiche] = useState(0);
  const [agenzie, setAgenzie] = useState([]);
  
  // Stati per assegnazione multipla
  const [selectedPratiche, setSelectedPratiche] = useState([]);
  const [showAssegnazioneModal, setShowAssegnazioneModal] = useState(false);
  const [assegnazioneStep, setAssegnazioneStep] = useState(1);
  const [agentiDisponibili, setAgentiDisponibili] = useState([]);
  const [assegnazioneConfig, setAssegnazioneConfig] = useState({
    modalita: 'intelligente', // 'intelligente' o 'manuale'
    agentiSelezionati: [],
    distribuzioneIntelligente: []
  });
  const [isAssigning, setIsAssigning] = useState(false);

  // Recupera utente corrente da localStorage
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    user = null;
  }
  const ruoloUtente = (user?.role || user?.ruolo || '').toLowerCase();
  const isAdmin = ruoloUtente === 'admin';
  const isAgenzia = ruoloUtente === 'agenzia';
  const isSubagente = ruoloUtente === 'subagente' || ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale';

  // Hook per gestire la gerarchia della rete
  const { userNode, subordinateNodes, allNodes, canViewNode, loading: hierarchyLoading } = useNetworkHierarchy(user);
  
  // Debug per l'hook useNetworkHierarchy
  //console.log('🔍 DEBUG useNetworkHierarchy:', {
  //  user: user ? { id: user.id, username: user.username, nome: user.nome, cognome: user.cognome, role: user.role } : null,
  //  userNode: userNode ? { id: userNode.id, nome: userNode.nome, tipoNodo: userNode.tipoNodo } : null,
  //  subordinateNodesCount: subordinateNodes.length,
  //  subordinateNodes: subordinateNodes.map(n => ({ id: n.id, nome: n.nome, utenteId: n.utenteId })),
  //  isAdmin,
  //  isAgenzia,
  //  isSubagente
  //});

  // Funzione per determinare se l'utente può vedere una pratica
  const canViewPratica = (pratica) => {
    const canView = canUserViewPratica(pratica, user, subordinateNodes, isAdmin, userNode);
    
    // Debug logging
    const praticaAgenteId = pratica.idAgente;
    const userRole = user?.role || user?.ruolo || '';
    
    if (!canView) {
      if (!praticaAgenteId) {
        console.log(`🔒 Pratica ${pratica.id} non assegnata - nascosta per ${userRole}`);
      } else {
        console.log(`🔒 Pratica ${pratica.id} nascosta: agente ${praticaAgenteId} non accessibile per ${userRole} (user.id: ${user.id})`);
      }
    } else {
      if (userRole === 'agenzia') {
        console.log(`✅ Pratica ${pratica.id} visibile: agenzia primaria (${userNode?.id}) vede pratica della sua agenzia (${pratica.idAgenziaPrimaria})`);
      } else if (user.id === praticaAgenteId) {
        console.log(`✅ Pratica ${pratica.id} visibile: assegnata all'utente corrente (${user.id})`);
      } else {
        const subordinato = subordinateNodes.find(node => node.utenteId === praticaAgenteId);
        if (subordinato) {
          console.log(`✅ Pratica ${pratica.id} visibile: assegnata al subordinato ${subordinato.nome} (utenteId: ${subordinato.utenteId})`);
        }
      }
    }
    
    return canView;
  };

  // Carica agenzie dal database
  const fetchAgenzie = async () => {
    try {
      const response = await authFetch(`${API_CONFIG.baseURL}/api/pratica/agenzie`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (response.ok && data.agenzie) {
        setAgenzie(data.agenzie);
      }
    } catch (error) {
      console.error('Errore nel caricamento agenzie:', error);
    }
  };

  // Carica pratiche
  const fetchPratiche = async () => {
    // Non caricare pratiche se la gerarchia non è ancora caricata
    if (hierarchyLoading) {
      //console.log('⏳ Attendo caricamento gerarchia prima di caricare pratiche...');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...(searchQuery && { search: searchQuery }),
        ...(filtroStato && { stato: filtroStato }),
        sort_by: 'dataAssegnazione',
        sort_order: 'DESC'
      });

      const response = await authFetch(`${API_CONFIG.baseURL}/api/pratica/all?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();

      let praticheFiltrate = data.pratiche || data.items || [];
      
      //console.log(`📊 Dati ricevuti dal backend:`, {
      //  pratiche: data.pratiche?.length || 0,
      //  items: data.items?.length || 0,
      //  total: data.pagination?.total || 0,
      //  user: user ? `${user.username} (${user.role})` : 'N/D'
      //});
      
      // Il backend dovrebbe già applicare i filtri di sicurezza tramite DataFilterMiddleware
      // Ma per sicurezza, applichiamo anche il filtro frontend
      if (!isAdmin) {
        const pratichePrimaFiltro = praticheFiltrate.length;
        praticheFiltrate = praticheFiltrate.filter(pratica => canViewPratica(pratica));
        //console.log(`🔒 Filtro frontend: ${pratichePrimaFiltro} -> ${praticheFiltrate.length} pratiche`);
      } else {
        //console.log('👑 Admin: nessun filtro applicato');
      }
      //console.log(`✅ Pratiche caricate: ${praticheFiltrate.length} (filtrate da ${data.pratiche?.length || 0})`);
      setPratiche(praticheFiltrate);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalPratiche(praticheFiltrate.length); // Usa il numero filtrato
      
      // Pulisci selezioni non più valide (pratiche assegnate o non più presenti)
      setSelectedPratiche(prev => {
        const idsPraticheSelecionabili = praticheFiltrate
          .filter(p => !p.idAgente)
          .map(p => p.id);
        return prev.filter(id => idsPraticheSelecionabili.includes(id));
      });
    } catch (error) {
      console.error('Errore nel caricamento pratiche:', error);
    } finally {
      setLoading(false);
    }
  };

  // Eliminazione definitiva di una pratica (solo admin) — utile per le pratiche di prova
  const handleEliminaPratica = async (pratica) => {
    const etichetta = pratica.codicePratica || `#${pratica.id}`;
    if (!window.confirm(`Eliminare DEFINITIVAMENTE la pratica ${etichetta}?\n\nVerranno rimossi anche preventivi, documenti e contratti collegati. L'operazione non è reversibile.`)) {
      return;
    }
    try {
      const response = await authFetch(`${API_CONFIG.baseURL}/api/pratica/${pratica.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success !== false) {
        toast.success(`Pratica ${etichetta} eliminata`);
        setPratiche(prev => prev.filter(p => p.id !== pratica.id));
      } else {
        toast.error(data.error || 'Errore durante l\'eliminazione della pratica');
      }
    } catch (error) {
      console.error('Errore eliminazione pratica:', error);
      toast.error('Errore durante l\'eliminazione della pratica');
    }
  };

  // Funzioni per assegnazione multipla
  const handleSelectPratica = (praticaId) => {
    // Trova la pratica per verificare se è già assegnata
    const pratica = pratiche.find(p => p.id === praticaId);
    
    // Non permettere selezione di pratiche già assegnate
    if (pratica && pratica.idAgente) {
      console.log(`⚠️ Pratica ${praticaId} già assegnata all'agente ${pratica.idAgente}`);
      return;
    }
    
    setSelectedPratiche(prev => {
      if (prev.includes(praticaId)) {
        return prev.filter(id => id !== praticaId);
      } else {
        return [...prev, praticaId];
      }
    });
  };

  // Filtra pratiche selezionabili (non ancora assegnate)
  const praticheSelecionabili = pratiche.filter(p => !p.idAgente);
  
  const handleSelectAll = () => {
    if (selectedPratiche.length === praticheSelecionabili.length && praticheSelecionabili.length > 0) {
      setSelectedPratiche([]);
    } else {
      setSelectedPratiche(praticheSelecionabili.map(p => p.id));
    }
  };

  const fetchAgentiDisponibili = async () => {
    try {
      // Carica gli agenti disponibili per l'agenzia corrente
      const response = await authFetch(`${API_CONFIG.baseURL}/api/network/agenti-disponibili`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAgentiDisponibili(data.agenti || []);
      }
    } catch (error) {
      console.error('Errore caricamento agenti:', error);
    }
  };

  const calcolaDistribuzioneIntelligente = () => {
    // Calcola distribuzione basata su conversione e carico di lavoro
    const praticheDaAssegnare = pratiche.filter(p => selectedPratiche.includes(p.id));
    const agentiOrdinati = [...agentiDisponibili].sort((a, b) => {
      // Ordina per conversione decrescente, poi per carico crescente
      const convA = a.statistiche?.conversione || 0;
      const convB = b.statistiche?.conversione || 0;
      const caricoA = a.statistiche?.caricoPratiche || 0;
      const caricoB = b.statistiche?.caricoPratiche || 0;
      
      if (convA !== convB) return convB - convA; // Conversione decrescente
      return caricoA - caricoB; // Carico crescente
    });

    const distribuzione = [];
    let agenteIndex = 0;

    praticheDaAssegnare.forEach((pratica, index) => {
      if (agentiOrdinati.length > 0) {
        const agente = agentiOrdinati[agenteIndex % agentiOrdinati.length];
        distribuzione.push({
          praticaId: pratica.id,
          agente: agente,
          motivazione: `Conversione: ${agente.statistiche?.conversione || 0}%, Carico: ${agente.statistiche?.caricoPratiche || 0} pratiche`
        });
        agenteIndex++;
      }
    });

    setAssegnazioneConfig(prev => ({
      ...prev,
      distribuzioneIntelligente: distribuzione
    }));
  };

  const apriAssegnazioneModal = () => {
    if (selectedPratiche.length === 0) {
      toast.warning('⚠️ Seleziona almeno una pratica da assegnare');
      return;
    }
    
    // Verifica che tutte le pratiche selezionate siano effettivamente selezionabili
    const praticheDaAssegnare = pratiche.filter(p => 
      selectedPratiche.includes(p.id) && !p.idAgente
    );
    
    if (praticheDaAssegnare.length !== selectedPratiche.length) {
      console.warn('Alcune pratiche selezionate sono già assegnate');
      // Rimuovi automaticamente le pratiche già assegnate dalla selezione
      setSelectedPratiche(praticheDaAssegnare.map(p => p.id));
      if (praticheDaAssegnare.length === 0) {
        toast.error('❌ Nessuna pratica disponibile per l\'assegnazione');
        return;
      }
      toast.info(`ℹ️ Rimosse ${selectedPratiche.length - praticheDaAssegnare.length} pratiche già assegnate`);
    }
    
    fetchAgentiDisponibili();
    setShowAssegnazioneModal(true);
    setAssegnazioneStep(1);
  };

  const eseguiAssegnazione = async () => {
    // Previeni chiamate multiple
    if (isAssigning) {
      toast.info('⏳ Assegnazione già in corso...');
      return;
    }

    setIsAssigning(true);
    
    try {
      const distribuzione = assegnazioneConfig.modalita === 'intelligente' 
        ? assegnazioneConfig.distribuzioneIntelligente
        : assegnazioneConfig.agentiSelezionati;

      const response = await authFetch(`${API_CONFIG.baseURL}/api/pratica/assegnazione-multipla-pratiche`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          assegnazioni: distribuzione
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          // Mostra toast di successo con dettagli
          const successCount = data.risultati?.length || 0;
          const errorCount = data.errori?.length || 0;
          
          if (errorCount === 0) {
            toast.success(`🎉 Assegnazione completata! ${successCount} pratiche assegnate con successo`);
          } else {
            toast.warning(`⚠️ Assegnazione parziale: ${successCount} successi, ${errorCount} errori`);
            
            // Mostra dettagli errori
            if (data.errori && data.errori.length > 0) {
              data.errori.forEach(errore => {
                toast.error(errore);
              });
            }
          }
          
          // Chiudi modal e aggiorna lista
          setShowAssegnazioneModal(false);
          setSelectedPratiche([]);
          fetchPratiche();
          
        } else {
          toast.error(`❌ Errore nell'assegnazione: ${data.message || 'Errore sconosciuto'}`);
        }
      } else {
        const error = await response.json();
        toast.error(`❌ Errore server: ${error.message || 'Errore di comunicazione'}`);
      }
    } catch (error) {
      console.error('Errore assegnazione:', error);
      toast.error('❌ Errore di connessione durante l\'assegnazione delle pratiche');
    } finally {
      setIsAssigning(false);
    }
  };

  useEffect(() => {
    // Carica agenzie una sola volta al mount
    fetchAgenzie();
  }, []);

  useEffect(() => {
    fetchPratiche();
  }, [currentPage, searchQuery, filtroStato, hierarchyLoading]);

  // Funzioni di utilità
  const getStatoInfo = (stato) => {
    const stati = {
      // Workflow ufficiale
      'pratica_avviata': { label: 'Pratica Avviata', color: 'bg-gradient-to-r from-blue-50/80 to-blue-100/60 text-blue-700 border-blue-200/50 backdrop-blur-sm' },
      'attesa_preventivo': { label: 'Attesa Preventivo', color: 'bg-gradient-to-r from-purple-50/80 to-purple-100/60 text-purple-700 border-purple-200/50 backdrop-blur-sm' },
      'valutazione_cliente': { label: 'Valutazione Cliente', color: 'bg-gradient-to-r from-yellow-50/80 to-yellow-100/60 text-yellow-700 border-yellow-200/50 backdrop-blur-sm' },
      'attesa_documenti_cliente': { label: 'Attesa Documenti Cliente', color: 'bg-gradient-to-r from-orange-50/80 to-orange-100/60 text-orange-700 border-orange-200/50 backdrop-blur-sm' },
      'richiesta_esito': { label: 'Richiesta Esito', color: 'bg-gradient-to-r from-amber-50/80 to-amber-100/60 text-amber-700 border-amber-200/50 backdrop-blur-sm' },
      'pratica_in_valutazione': { label: 'Pratica in Valutazione', color: 'bg-gradient-to-r from-indigo-50/80 to-indigo-100/60 text-indigo-700 border-indigo-200/50 backdrop-blur-sm' },
      'attesa_firma_contratto': { label: 'Attesa Firma Contratto', color: 'bg-gradient-to-r from-green-50/80 to-green-100/60 text-green-700 border-green-200/50 backdrop-blur-sm' },
      'contratto_attivo': { label: 'Contratto Attivo', color: 'bg-gradient-to-r from-emerald-50/80 to-emerald-100/60 text-emerald-700 border-emerald-200/50 backdrop-blur-sm' },
      
      // Stati speciali
      'richiesta_integrazione': { label: 'Richiesta Integrazione', color: 'bg-gradient-to-r from-red-50/80 to-red-100/60 text-red-700 border-red-200/50 backdrop-blur-sm' },
      'pratica_chiusa': { label: 'Pratica Chiusa', color: 'bg-gradient-to-r from-gray-50/80 to-gray-100/60 text-gray-700 border-gray-200/50 backdrop-blur-sm' },
      'esito_negativo': { label: 'Esito Negativo', color: 'bg-gradient-to-r from-rose-50/80 to-rose-100/60 text-rose-700 border-rose-200/50 backdrop-blur-sm' },
      'condizionata': { label: 'Condizionata', color: 'bg-gradient-to-r from-yellow-50/80 to-yellow-100/60 text-yellow-700 border-yellow-200/50 backdrop-blur-sm' },

      // Stati legacy per compatibilità (nascosti dai filtri)
      'attesa_approvazione_cliente': { label: 'Attesa Approvazione Cliente', color: 'bg-gradient-to-r from-yellow-50/80 to-yellow-100/60 text-yellow-700 border-yellow-200/50 backdrop-blur-sm' }
    };
    return stati[stato] || { label: stato, color: 'bg-gradient-to-r from-gray-50/80 to-gray-100/60 text-gray-700 border-gray-200/50 backdrop-blur-sm' };
  };

  const getPrioritaColor = (priorita) => {
    const colors = {
      'bassa': 'text-gray-500',
      'normale': 'text-blue-600',
      'alta': 'text-orange-600',
      'urgente': 'text-red-600'
    };
    return colors[priorita] || 'text-gray-500';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getAgenziaName = (idAgenzia) => {
    // Prima cerca nell'array agenzie (caricato dall'API)
    const agenzia = agenzie.find(a => a.id === idAgenzia);
    if (agenzia) return agenzia.nome;
    
    // Altrimenti cerca in allNodes (dalla gerarchia rete)
    const nodo = allNodes.find(n => n.id === idAgenzia);
    if (!nodo) return `Nodo ${idAgenzia}`;
    
    // Se è un'agenzia primaria, mostra solo il nome
    if (nodo.tipo === 'agenzia_primaria') {
      return nodo.nome;
    }
    
    // Solo l'admin vede il percorso gerarchico completo
    // Gli altri ruoli vedono solo l'agenzia primaria
    if (!isAdmin) {
      // Risali fino all'agenzia primaria
      let current = nodo;
      while (current && current.tipo !== 'agenzia_primaria') {
        current = allNodes.find(n => n.id === current.parentId);
      }
      return current ? current.nome : nodo.nome;
    }
    
    // SOLO PER ADMIN: costruisci il percorso gerarchico completo
    if (nodo.parentId) {
      const parent = allNodes.find(n => n.id === nodo.parentId);
      if (parent && parent.tipo === 'agenzia_primaria') {
        return `${parent.nome} - ${nodo.nome.replace('Agente ', '')}`;
      } else if (parent) {
        // Ricorsivamente trova l'agenzia primaria
        let current = parent;
        const path = [nodo.nome.replace('Agente ', '').replace('Team Leader ', '')];
        
        while (current && current.tipo !== 'agenzia_primaria') {
          path.unshift(current.nome.replace('Agente ', '').replace('Team Leader ', ''));
          current = allNodes.find(n => n.id === current.parentId);
        }
        
        if (current && current.tipo === 'agenzia_primaria') {
          path.unshift(current.nome);
          return path.join(' - ');
        }
      }
    }
    
    return nodo.nome;
  };

  const getScadenzaInfo = (dataScadenza) => {
    if (!dataScadenza) return null;
    
    const oggi = new Date();
    const scadenza = new Date(dataScadenza);
    const diffGiorni = Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24));
    
    if (diffGiorni < 0) return { text: `Scaduta da ${Math.abs(diffGiorni)}g`, color: 'text-red-600 font-semibold' };
    if (diffGiorni === 0) return { text: 'Scade oggi', color: 'text-orange-600 font-semibold' };
    if (diffGiorni <= 3) return { text: `${diffGiorni}g`, color: 'text-orange-600 font-medium' };
    return { text: `${diffGiorni}g`, color: 'text-gray-600' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30">
      <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-8 space-y-6">
        
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                {isAdmin ? 'Tutte le Pratiche' : 
                 isAgenzia ? 'Le Tue Pratiche' : 
                 'Pratiche Assegnate'}
              </h1>
              <p className="mt-2 text-gray-600">
                {pratiche.length} pratiche 
                {isAdmin ? ' totali' : 
                 isAgenzia ? ' della tua agenzia' : 
                 ' assegnate a te'}
              </p>
            </div>
            <Link
              to="/nlt/lead"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transform hover:scale-105 transition-all duration-200"
            >
              <ChevronRightIcon className="h-5 w-5 mr-2" />
              Gestisci Lead
            </Link>
          </div>
        </div>

        {/* Filtri */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50">
          <div className="bg-gradient-to-r from-gray-50/50 to-white/50 p-6 rounded-t-2xl border-b border-gray-100/50">
            <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-6 space-y-4 lg:space-y-0">
              {/* Ricerca */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cerca pratiche..."
                    className="w-full pl-12 pr-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 placeholder-gray-400"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Filtro Stato */}
              <select
                className="px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 font-medium text-gray-700"
                value={filtroStato}
                onChange={(e) => setFiltroStato(e.target.value)}
              >
                <option value="">Tutti gli stati</option>
                <option value="pratica_avviata">Pratica Avviata</option>
                <option value="attesa_preventivo">Attesa Preventivo</option>
                <option value="valutazione_cliente">Valutazione Cliente</option>
                <option value="attesa_documenti_cliente">Attesa Documenti Cliente</option>
                <option value="richiesta_esito">Richiesta Esito</option>
                <option value="pratica_in_valutazione">Pratica in Valutazione</option>
                <option value="attesa_firma_contratto">Attesa Firma Contratto</option>
                <option value="contratto_attivo">Contratto Attivo</option>
                <option value="richiesta_integrazione">Richiesta Integrazione</option>
                <option value="pratica_chiusa">Pratica Chiusa</option>
                <option value="esito_negativo">Esito Negativo</option>
                <option value="condizionata">Condizionata</option>
              </select>

              {/* Reset */}
              {(searchQuery || filtroStato) && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFiltroStato('');
                  }}
                  className="px-6 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm text-gray-600 hover:text-gray-900 hover:bg-white/90 transition-all duration-200 font-medium"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Lista Pratiche */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50 overflow-hidden">
          {loading ? (
            <div className="p-16 text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-blue-200 border-t-blue-600"></div>
              <p className="mt-6 text-gray-600 font-medium">Caricamento pratiche...</p>
            </div>
          ) : pratiche.length > 0 ? (
            <div className="divide-y divide-gray-100/50">
              {/* Barra azioni assegnazione multipla - solo per agenzia primaria */}
            {isAgenzia && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedPratiche.length === praticheSelecionabili.length && praticheSelecionabili.length > 0}
                        onChange={handleSelectAll}
                        disabled={praticheSelecionabili.length === 0}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium">
                        Seleziona tutte disponibili ({selectedPratiche.length}/{praticheSelecionabili.length})
                      </span>
                    </label>
                    
                    {pratiche.length - praticheSelecionabili.length > 0 && (
                      <span className="text-xs text-gray-500">
                        ({pratiche.length - praticheSelecionabili.length} già assegnate)
                      </span>
                    )}
                  </div>
                  
                  {selectedPratiche.length > 0 && (
                    <button
                      onClick={apriAssegnazioneModal}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                    >
                      Assegna {selectedPratiche.length} Pratiche
                    </button>
                  )}
                </div>
              </div>
            )}

            {pratiche.map((pratica) => {
                const statoInfo = getStatoInfo(pratica.statoPratica);
                const scadenzaInfo = getScadenzaInfo(pratica.dataScadenza);
                
                // Debug temporaneo
                //console.log('🐛 DEBUG Pratica:', pratica);
                //console.log('🐛 DEBUG Pratica ID:', pratica.id);
                
                return (
                  <div key={pratica.id} className={`p-6 hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-white/30 transition-all duration-200 group relative ${pratica.idAgente ? 'opacity-75 bg-gray-50/50' : ''}`}>
                    
                    {/* Checkbox per selezione - solo per agenzia primaria */}
                    {isAgenzia && (
                      <div className="absolute top-4 left-4 z-10">
                        <input
                          type="checkbox"
                          checked={selectedPratiche.includes(pratica.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectPratica(pratica.id);
                          }}
                          disabled={!!pratica.idAgente}
                          className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                            pratica.idAgente ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                          }`}
                          title={pratica.idAgente ? 'Pratica già assegnata' : 'Seleziona per assegnazione multipla'}
                        />
                      </div>
                    )}
                    
                    {/* Indicatore pratica già assegnata */}
                    {pratica.idAgente && isAgenzia && (
                      <div className="absolute top-4 right-4 z-10">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          ✓ Assegnata
                        </span>
                      </div>
                    )}

                    <div className={`flex items-start justify-between ${isAgenzia ? 'ml-8' : ''}`}>
                      
                      {/* Info Principale */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-6 mb-4">
                          {/* Codice Pratica */}
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-200">
                              {pratica.codicePratica}
                            </h3>
                            <p className="text-sm text-gray-500 font-medium">ID #{pratica.id}</p>
                          </div>

                          {/* Stato */}
                          <span className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold border ${statoInfo.color}`}>
                            {statoInfo.label}
                          </span>

                          {/* Priorità */}
                          <div className={`text-sm font-bold uppercase tracking-wide ${getPrioritaColor(pratica.priorita)}`}>
                            {pratica.priorita}
                          </div>
                        </div>

                        {/* Dettagli */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                          
                          {/* Cliente */}
                          <div className="flex items-start space-x-3">
                            <div className="p-2 bg-blue-50 rounded-lg">
                              <UserIcon className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-900 truncate">
                                {pratica.cliente?.ragione_sociale || 
                                 `${pratica.cliente?.nome || ''} ${pratica.cliente?.cognome || ''}`.trim() ||
                                 'Cliente non trovato'}
                              </p>
                              <p className="text-sm text-gray-600 truncate">{pratica.cliente?.email}</p>
                            </div>
                          </div>

                          {/* Vettura */}
                          <div className="flex items-start space-x-3">
                            <div className="p-2 bg-green-50 rounded-lg">
                              <TruckIcon className="h-5 w-5 text-green-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              {pratica.vettura ? (
                                <>
                                  <p className="font-semibold text-gray-900 truncate">{pratica.vettura.brand}</p>
                                  <p className="text-sm text-gray-600 truncate">{pratica.vettura.modello}</p>
                                </>
                              ) : (
                                <p className="text-gray-500 italic">Nessuna vettura</p>
                              )}
                            </div>
                          </div>

                          {/* Data Assegnazione */}
                          <div className="flex items-start space-x-3">
                            <div className="p-2 bg-purple-50 rounded-lg">
                              <CalendarIcon className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Assegnata</p>
                              <p className="text-sm text-gray-600">{formatDate(pratica.dataAssegnazione)}</p>
                            </div>
                          </div>

                          {/* Agenzia */}
                          <div className="flex items-start space-x-3">
                            <div className="p-2 bg-orange-50 rounded-lg">
                              <div className="h-5 w-5 bg-orange-600 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">A</span>
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-900 truncate">{getAgenziaName(pratica.idAgenziaPrimaria)}</p>
                              <p className="text-sm text-gray-600">Rete</p>
                            </div>
                          </div>
                        </div>

                        {/* Info Aggiuntive */}
                        <div className="flex flex-wrap items-center gap-6 text-sm mb-3">
                          {pratica.importoStimato && (
                            <div className="bg-green-50/50 px-3 py-1.5 rounded-lg border border-green-200/50">
                              <span className="text-gray-600">Importo: </span>
                              <span className="font-bold text-green-700">€ {parseFloat(pratica.importoStimato).toLocaleString()}</span>
                            </div>
                          )}
                          
                          {scadenzaInfo && (
                            <div className="bg-orange-50/50 px-3 py-1.5 rounded-lg border border-orange-200/50">
                              <span className="text-gray-600">Scadenza: </span>
                              <span className={scadenzaInfo.color}>{scadenzaInfo.text}</span>
                            </div>
                          )}

                          {pratica.temperaturaOriginale && (
                            <div className="bg-blue-50/50 px-3 py-1.5 rounded-lg border border-blue-200/50">
                              <span className="text-gray-600">Temperatura: </span>
                              <span className="font-semibold text-blue-700">{pratica.temperaturaOriginale}</span>
                            </div>
                          )}
                        </div>

                        {/* Note */}
                        {pratica.note && (
                          <div className="mt-4 p-4 bg-gradient-to-r from-gray-50/50 to-white/50 backdrop-blur-sm rounded-xl border border-gray-100/50">
                            <p className="text-sm text-gray-700 leading-relaxed">{pratica.note}</p>
                          </div>
                        )}
                      </div>

                      {/* Azioni */}
                      <div className="ml-8 flex-shrink-0 flex items-start gap-2">
                        <button
                          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl font-medium shadow-lg shadow-gray-500/25 hover:shadow-xl hover:shadow-gray-500/30 transform hover:scale-105 transition-all duration-200"
                          onClick={() => {
                            window.open(`/nlt/pratiche/${pratica.id}`, '_blank');
                          }}
                        >
                          <EyeIcon className="h-5 w-5 mr-2" />
                          Dettagli
                        </button>
                        {isAdmin && (
                          <button
                            className="inline-flex items-center justify-center w-11 h-11 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors"
                            title="Elimina pratica"
                            onClick={() => handleEliminaPratica(pratica)}
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-16 text-center">
              <div className="mx-auto h-16 w-16 text-gray-400 mb-6">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Nessuna pratica trovata</h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Le pratiche verranno visualizzate qui una volta assegnate dalle lead.
              </p>
              <Link
                to="/nlt/lead"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transform hover:scale-105 transition-all duration-200"
              >
                Gestisci Lead
              </Link>
            </div>
          )}

          {/* Paginazione */}
          {totalPages > 1 && (
            <div className="bg-gradient-to-r from-gray-50/50 to-white/50 px-6 py-5 border-t border-gray-100/50 rounded-b-2xl">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">
                  Pagina {currentPage} di {totalPages}
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-6 py-2.5 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl font-medium text-gray-700 hover:bg-white/90 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/70 disabled:hover:shadow-none transition-all duration-200"
                  >
                    Precedente
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-6 py-2.5 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl font-medium text-gray-700 hover:bg-white/90 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/70 disabled:hover:shadow-none transition-all duration-200"
                  >
                    Successiva
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal Assegnazione Multipla */}
      <AssegnazioneMultiplaModal
        show={showAssegnazioneModal}
        onClose={() => setShowAssegnazioneModal(false)}
        step={assegnazioneStep}
        setStep={setAssegnazioneStep}
        selectedPratiche={selectedPratiche}
        pratiche={pratiche}
        agentiDisponibili={agentiDisponibili}
        assegnazioneConfig={assegnazioneConfig}
        setAssegnazioneConfig={setAssegnazioneConfig}
        calcolaDistribuzioneIntelligente={calcolaDistribuzioneIntelligente}
        eseguiAssegnazione={eseguiAssegnazione}
        isAssigning={isAssigning}
      />
    </div>
  );
};

// Componente Modal per Assegnazione Multipla
const AssegnazioneMultiplaModal = ({ 
  show, 
  onClose, 
  step, 
  setStep, 
  selectedPratiche, 
  pratiche, 
  agentiDisponibili, 
  assegnazioneConfig, 
  setAssegnazioneConfig, 
  calcolaDistribuzioneIntelligente, 
  eseguiAssegnazione,
  isAssigning 
}) => {
  if (!show) return null;

  const praticheDaAssegnare = pratiche.filter(p => selectedPratiche.includes(p.id));

  const nextStep = () => {
    if (step === 1) {
      if (assegnazioneConfig.modalita === 'intelligente') {
        calcolaDistribuzioneIntelligente();
      }
      setStep(2);
    } else if (step === 2) {
      if (assegnazioneConfig.modalita === 'manuale') {
        calcolaDistribuzioneManuale();
      }
      setStep(3);
    }
  };

  const calcolaDistribuzioneManuale = () => {
    const praticheDaAssegnare = pratiche.filter(p => selectedPratiche.includes(p.id));
    const distribuzione = [];
    
    let praticaIndex = 0;
    
    // Per ogni agente selezionato, assegna il numero di pratiche richiesto
    assegnazioneConfig.agentiSelezionati.forEach(({ agente, count }) => {
      for (let i = 0; i < count && praticaIndex < praticheDaAssegnare.length; i++) {
        const pratica = praticheDaAssegnare[praticaIndex];
        distribuzione.push({
          praticaId: pratica.id,
          agente: agente,
          motivazione: `Assegnazione manuale: ${count} pratica/e all'agente`
        });
        praticaIndex++;
      }
    });

    setAssegnazioneConfig(prev => ({
      ...prev,
      distribuzioneIntelligente: distribuzione
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-4">
            Assegnazione Multipla - Step {step}/3
          </h2>
          
          {step === 1 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Seleziona modalità di assegnazione
              </h3>
              <p className="mb-4">
                {selectedPratiche.length} pratiche selezionate per l'assegnazione
              </p>
              
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="modalita"
                    value="intelligente"
                    checked={assegnazioneConfig.modalita === 'intelligente'}
                    onChange={(e) => setAssegnazioneConfig(prev => ({
                      ...prev,
                      modalita: e.target.value
                    }))}
                    className="mr-2"
                  />
                  <div>
                    <div className="font-medium">Assegnazione Intelligente</div>
                    <div className="text-sm text-gray-600">
                      Le pratiche vengono assegnate automaticamente agli agenti con migliore conversione
                    </div>
                  </div>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="radio" 
                    name="modalita"
                    value="manuale"
                    checked={assegnazioneConfig.modalita === 'manuale'}
                    onChange={(e) => setAssegnazioneConfig(prev => ({
                      ...prev,
                      modalita: e.target.value
                    }))}
                    className="mr-2"
                  />
                  <div>
                    <div className="font-medium">Assegnazione Manuale</div>
                    <div className="text-sm text-gray-600">
                      Scegli manualmente a quale agente assegnare ogni pratica
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                {assegnazioneConfig.modalita === 'intelligente' ? 'Agenti Disponibili' : 'Seleziona Agenti per Assegnazione Manuale'}
              </h3>
              <div className="space-y-3 mb-6">
                {agentiDisponibili.map(agente => (
                  <div key={agente.id} className="border p-3 rounded">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{agente.nome} {agente.cognome}</div>
                        <div className="text-sm text-gray-600">
                          Conversione: {agente.statistiche?.conversione || 0}% | 
                          Pratiche attive: {agente.statistiche?.caricoPratiche || 0}
                        </div>
                      </div>
                      
                      {assegnazioneConfig.modalita === 'manuale' && (
                        <div className="flex items-center space-x-2">
                          <label className="text-sm font-medium">Pratiche da assegnare:</label>
                          <input
                            type="number"
                            min="0"
                            max={selectedPratiche.length}
                            defaultValue="0"
                            onChange={(e) => {
                              const count = parseInt(e.target.value) || 0;
                              const maxConsentito = selectedPratiche.length;
                              
                              // Calcola il totale assegnato agli altri agenti
                              const altriAssegnati = (assegnazioneConfig.agentiSelezionati || [])
                                .filter(a => a.agente.id !== agente.id)
                                .reduce((sum, a) => sum + a.count, 0);
                              
                              // Limita il count per non superare il totale disponibile
                              const countEffettivo = Math.min(count, maxConsentito - altriAssegnati);
                              
                              if (countEffettivo !== count && count > 0) {
                                e.target.value = countEffettivo;
                                toast.warning(`⚠️ Massimo ${maxConsentito - altriAssegnati} pratiche disponibili per ${agente.nome} ${agente.cognome}`);
                              }
                              
                              setAssegnazioneConfig(prev => {
                                const newAssegnazioni = [...(prev.agentiSelezionati || [])];
                                const existingIndex = newAssegnazioni.findIndex(a => a.agente.id === agente.id);
                                
                                if (countEffettivo > 0) {
                                  if (existingIndex >= 0) {
                                    newAssegnazioni[existingIndex].count = countEffettivo;
                                  } else {
                                    newAssegnazioni.push({ agente, count: countEffettivo });
                                  }
                                } else {
                                  if (existingIndex >= 0) {
                                    newAssegnazioni.splice(existingIndex, 1);
                                  }
                                }
                                
                                return {
                                  ...prev,
                                  agentiSelezionati: newAssegnazioni
                                };
                              });
                            }}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-center"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Riepilogo Assegnazione</h3>
              <div className="space-y-3">
                {assegnazioneConfig.distribuzioneIntelligente.map((item, index) => {
                  const pratica = pratiche.find(p => p.id === item.praticaId);
                  return (
                    <div key={index} className="border p-3 rounded flex justify-between">
                      <div>
                        <div className="font-medium">
                          Pratica #{pratica?.codicePratica || item.praticaId}
                        </div>
                        <div className="text-sm text-gray-600">
                          Cliente: {pratica?.cliente?.nome || 'N/D'} {pratica?.cliente?.cognome || ''}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">
                          {item.agente.nome} {item.agente.cognome}
                        </div>
                        <div className="text-sm text-gray-600">
                          {item.motivazione}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          <div className="flex justify-between mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              Annulla
            </button>
            
            <div className="space-x-2">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Indietro
                </button>
              )}
              
              {step < 3 ? (
                <button
                  onClick={nextStep}
                  disabled={step === 2 && assegnazioneConfig.modalita === 'manuale' && 
                    (!assegnazioneConfig.agentiSelezionati || 
                     assegnazioneConfig.agentiSelezionati.length === 0 ||
                     assegnazioneConfig.agentiSelezionati.reduce((sum, a) => sum + a.count, 0) === 0)}
                  className={`px-4 py-2 rounded ${
                    step === 2 && assegnazioneConfig.modalita === 'manuale' && 
                    (!assegnazioneConfig.agentiSelezionati || 
                     assegnazioneConfig.agentiSelezionati.length === 0 ||
                     assegnazioneConfig.agentiSelezionati.reduce((sum, a) => sum + a.count, 0) === 0)
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Avanti
                </button>
              ) : (
                <button
                  onClick={eseguiAssegnazione}
                  disabled={isAssigning}
                  className={`px-4 py-2 rounded ${
                    isAssigning ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isAssigning ? 'Assegnazione in corso...' : 'Conferma Assegnazione'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PratichePage; 